import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STYLE_OPTIONS = ['modern', 'minimal', 'bold', 'elegant', 'playful', 'corporate'];

// Self-contained "Create New Website" modal. Owns its own form state and
// navigates to the editor on success, so the parent only toggles visibility.
export const NewProjectModal = ({ onClose }) => {
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState('');
  const [projectPrompt, setProjectPrompt] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#7dd3fc');
  const [styleChoice, setStyleChoice] = useState('modern');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = useCallback(async () => {
    if (!projectName || !projectPrompt) {
      toast.error('Please fill in project name and description');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/website/generate`, {
        project_name: projectName,
        prompt: projectPrompt,
        style_preferences: { primaryColor, style: styleChoice }
      }, {
        withCredentials: true
      });

      toast.success('Website generated successfully!');
      navigate(`/admin/editor/${response.data.project_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate website');
    } finally {
      setIsGenerating(false);
    }
  }, [projectName, projectPrompt, primaryColor, styleChoice, navigate]);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#302f2c] border border-[#1e3a5f]/50 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#1e3a5f]/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#7dd3fc] rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#1a1c1b]" />
              </div>
              <div>
                <h2 className="text-[#7dd3fc] font-bold text-xl">Create New Website</h2>
                <p className="text-[#888680] text-sm">Describe your website and let AI build it</p>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="close-new-project-btn"
              className="text-[#888680] hover:text-[#7dd3fc]"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleGenerate(); }} className="p-6 space-y-6">
          <div>
            <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">
              Project Name
            </label>
            <Input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="My Awesome Website"
              data-testid="project-name-input"
              className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] placeholder:text-[#888680]/50 focus:border-[#7dd3fc] h-12"
            />
          </div>

          <div>
            <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">
              Describe Your Website
            </label>
            <Textarea
              value={projectPrompt}
              onChange={(e) => setProjectPrompt(e.target.value)}
              placeholder="E.g., A modern portfolio website for a photographer with a dark theme, gallery section, about page, and contact form. Should feel elegant and minimalist."
              rows={6}
              data-testid="project-prompt-textarea"
              className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] placeholder:text-[#888680]/50 focus:border-[#7dd3fc] resize-none"
            />
            <p className="text-[#888680] text-xs mt-2">
              Be specific about the type of website, style, sections, and any special features you want.
            </p>
          </div>

          <div>
            <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">
              Primary Color
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                data-testid="primary-color-input"
                className="w-12 h-12 rounded cursor-pointer bg-transparent border-2 border-[#1e3a5f]"
              />
              <span className="text-[#888680]">{primaryColor}</span>
            </div>
          </div>

          <div>
            <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">
              Style
            </label>
            <div className="grid grid-cols-3 gap-3">
              {STYLE_OPTIONS.map((style) => (
                <button
                  key={style}
                  type="button"
                  onClick={() => setStyleChoice(style)}
                  data-testid={`style-choice-${style}`}
                  className={`px-4 py-3 rounded-lg border font-medium capitalize transition-all ${
                    styleChoice === style
                      ? 'bg-[#7dd3fc] text-[#1a1c1b] border-[#7dd3fc]'
                      : 'bg-[#1a1c1b] text-[#888680] border-[#1e3a5f] hover:border-[#7dd3fc]/50'
                  }`}
                >
                  {style}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] hover:border-[#7dd3fc]/50 rounded-full py-6"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isGenerating}
              data-testid="generate-website-btn"
              className="flex-1 bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full py-6 font-semibold flex items-center justify-center gap-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles size={20} />
                  Generate Website
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
